import { useCallback, useEffect } from 'react';

import { useEnvironment } from '@wix/yoshi-flow-editor';

import { useAppSelector } from '@/store';
import { selectOneChatSettings } from '@/features';
import { EXPERIMENTS, SS_REPORTED_NEW_VISITOR } from '@/constants';
import { CurrentStatus } from '@/features/oneChatSettings/model';

import {
  useRootProps,
  useWidgetServices,
} from 'AiAssistantWidget/Widget/contexts';
import { useIsExperimentEnabled } from 'AiAssistantWidget/experiments';

const useSSReportedNewVisitor = () => {
  const isReported = useCallback(
    () => sessionStorage.getItem(SS_REPORTED_NEW_VISITOR) === 'true',
    [],
  );

  const setIsReported = useCallback((value: boolean) => {
    sessionStorage.setItem(SS_REPORTED_NEW_VISITOR, value ? 'true' : 'false');
  }, []);

  return { isReported, setIsReported };
};

export const useReportNewVisitor = () => {
  const { visitorPresenceService } = useWidgetServices();
  const { isEditor, isSSR } = useEnvironment();
  const { page: { id: pageId, name: pageName } = {} } = useRootProps();
  const isReportNewVisitorExperimentEnabled = useIsExperimentEnabled(
    EXPERIMENTS.REPORT_NEW_VISITOR,
  );

  const currentStatus = useAppSelector(selectOneChatSettings.currentStatus);
  const isCurrentStatusOnline =
    currentStatus === CurrentStatus.AI_ASSISTANT_ONLINE ||
    currentStatus === CurrentStatus.MANUAL_ONLINE;

  const { isReported, setIsReported } = useSSReportedNewVisitor();

  useEffect(() => {
    if (isEditor || isSSR) {
      return;
    }
    if (isReported()) {
      return;
    }
    if (!isCurrentStatusOnline) {
      return;
    }
    if (!isReportNewVisitorExperimentEnabled) {
      return;
    }
    if (!pageId || !pageName) {
      return;
    }

    setIsReported(true);
    void visitorPresenceService
      .reportNewVisitor({ pageId, pageName })
      .catch(() => {
        setIsReported(false);
      });
  }, [
    isEditor,
    isSSR,
    isCurrentStatusOnline,
    isReportNewVisitorExperimentEnabled,
    pageId,
    pageName,
    visitorPresenceService,
    isReported,
    setIsReported,
  ]);
};
